import { useState, useEffect, useCallback } from 'react';
import { useChat } from '../context/ChatContext';
import { RefreshCw, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

import { API_BASE_URL as API } from '../utils/apiConfig';

function formatTimeAgo(dateString) {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata'
  });
}

function formatFullDate(dateString) {
  if (!dateString) return 'Never synced';
  
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Asia/Kolkata'
  }) + ' IST';
}

export default function SyncStatusBadge({ className = '' }) {
  const { selectedBrand } = useChat();
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSyncStatus = useCallback(async () => {
    if (!selectedBrand?.brand_id) return;
    
    try {
      const response = await fetch(`${API}/api/sync/status/${selectedBrand.brand_id}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.brand_id]);

  useEffect(() => {
    fetchSyncStatus();
    // Refresh every 2 minutes
    const interval = setInterval(fetchSyncStatus, 120000);
    return () => clearInterval(interval);
  }, [fetchSyncStatus]);

  if (loading) {
    return (
      <Badge variant="outline" className={`bg-muted/50 text-muted-foreground ${className}`}>
        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
        Loading...
      </Badge>
    );
  }

  const lastSync = syncStatus?.last_sync;
  const status = syncStatus?.status || 'unknown';
  const isRecent = lastSync && (new Date() - new Date(lastSync)) < 30 * 60000; // Within 30 mins

  const getStatusColor = () => {
    if (status === 'completed' && isRecent) return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
    if (status === 'completed') return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
    if (status === 'failed') return 'bg-red-500/10 text-red-400 border-red-500/30';
    if (status === 'syncing') return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
    return 'bg-muted/50 text-muted-foreground';
  };

  const getStatusIcon = () => {
    if (status === 'syncing') return <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />;
    if (status === 'completed' && isRecent) return <CheckCircle className="w-3 h-3 mr-1.5" />;
    if (status === 'failed') return <AlertCircle className="w-3 h-3 mr-1.5" />;
    return <Clock className="w-3 h-3 mr-1.5" />;
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`cursor-help ${getStatusColor()} ${className}`}
            data-testid="sync-status-badge"
          >
            {getStatusIcon()}
            <span className="text-xs">
              Data synced {formatTimeAgo(lastSync)}
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1 text-xs">
            <p className="font-medium">Last Sync: {formatFullDate(lastSync)}</p>
            <p className="text-muted-foreground">
              Status: <span className="capitalize">{status}</span>
            </p>
            {syncStatus?.stats?.stats && (
              <p className="text-muted-foreground">
                {syncStatus.stats.stats.total_customers?.toLocaleString()} customers, {' '}
                {syncStatus.stats.stats.total_transactions?.toLocaleString()} transactions
              </p>
            )}
            <p className="text-muted-foreground italic">
              Data syncs automatically every 15 minutes
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
